import { serializeError } from '@mono-farmart-web/common-utils/utils/serializeError';
import { createCapacitorBridge } from '@mono-farmart-web/capacitor-bridge';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import React, {
	createContext,
	useContext,
	useEffect,
	type ReactNode,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useModal } from '@mono-farmart-web/modal-context/context/ModalContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { primaryRoutes } from '@mono-farmart-web/common-utils';
import { useNavigation } from '@mono-farmart-web/navigation-context/context/Navigation';
import { checkAppName } from '@mono-farmart-web/common-utils/utils/common';
import { trackEvent } from '@mono-farmart-web/analytics/utils/moengage';
import Events from '@mono-farmart-web/analytics/constant/MoengageEvents';

const appName = checkAppName();

// Define a type for the context data
interface PlatformContextData {
	platform: 'APP' | 'WEB';
	appVersion: string;
	nativeBridgeV2Enabled: boolean;
}

const PlatformContext = createContext<PlatformContextData | undefined>(
	undefined,
);

// Define the props for the PlatformContextProvider component
interface PlatformContextProviderProps {
	children: ReactNode;
}

export function usePlatfromData() {
	return useContext(PlatformContext);
}

/**
 * True only inside the native shell's TOP frame. An embedded cross-origin frame
 * (e.g. `tracking-service` in a `track-consent` iframe) reports
 * `isNativePlatform() === true` but has no native plugin bridge, so `App`
 * listeners / `App.exitApp()` there reject with `"App" plugin is not
 * implemented`. Reading `window.top` across origins throws — that throw is the
 * signal that we are not the top frame.
 */
function isTopFrame(): boolean {
	if (typeof window === 'undefined') return true;
	try {
		return window.top === window.self;
	} catch {
		return false;
	}
}

/** Capacitor iOS/Android shell (top frame), not mobile browser or embedded iframe. */
function isNativeAppShell() {
	try {
		return Capacitor.isNativePlatform() && isTopFrame();
	} catch {
		return false;
	}
}

/** Capacitor Android/iOS runtime (`@capacitor/core` native platform), top frame only. */
function isCapacitorNativeShell(): boolean {
	try {
		return Capacitor.isNativePlatform() && isTopFrame();
	} catch {
		return false;
	}
}

interface AppVersionNativeResponse {
	appVersion: string;
}

export function PlatformContextProvider({
	children,
}: PlatformContextProviderProps) {
	// Constructed here rather than at module scope so that merely importing this
	// module has no native side effects (the bridge registers Capacitor `App`
	// listeners on construction). `createCapacitorBridge()` is a singleton, so
	// this stays a stable reference across renders.
	const bridge = useMemo(() => createCapacitorBridge(), []);

	const [appVersion, setAppVersion] = useState<string>('');
	const { pathname } = useLocation();

	const nativeBridgeV2Enabled = useMemo(() => isNativeAppShell(), []);

	const platformData: PlatformContextData = useMemo(
		() => ({
			platform: isNativeAppShell() ? 'APP' : 'WEB',
			appVersion,
			nativeBridgeV2Enabled,
		}),
		[appVersion, nativeBridgeV2Enabled],
	);

	useEffect(() => {
		console.log('[farmart-auth] platform context', platformData);
	}, [platformData]);

	const navigate = useNavigate();

	const { closeModal, getIsModalOpen, closeAllModal } = useModal();
	const { navigationConfig } = useNavigation();

	const getIsModalOpenRef = useRef(getIsModalOpen);
	const closeModalRef = useRef(closeModal);
	const navigationConfigRef = useRef(navigationConfig);
	const navigateRef = useRef(navigate);

	getIsModalOpenRef.current = getIsModalOpen;
	closeModalRef.current = closeModal;
	navigationConfigRef.current = navigationConfig;
	navigateRef.current = navigate;

	// The effect body is empty on purpose: `pathname` is what re-runs the
	// cleanup, closing open modals on navigation. Dropping it from the deps
	// would only close them on unmount.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname deliberately re-triggers the cleanup on route change
	useEffect(() => {
		return () => {
			closeAllModal();
		};
	}, [pathname, closeAllModal]);

	useEffect(() => {
		async function getAppVersion() {
			let appVersion = '';
			try {
				if (isNativeAppShell()) {
					const response =
						await bridge.invoke<AppVersionNativeResponse>('getCodePushVersion');
					appVersion = response.appVersion;
				}
			} catch (err) {
				bridge.printLog(
					'Unable to fetch App version',
					{
						errorMessage: serializeError(err),
					},
					'error',
				);
			} finally {
				setAppVersion(appVersion);
			}
		}
		getAppVersion();
	}, [bridge]);

	useEffect(() => {
		if (!isCapacitorNativeShell()) return;

		function handleCapacitorHardwareBack(ev: { canGoBack: boolean }) {
			const webViewCanGoBack = ev.canGoBack;
			const isModalOpen = getIsModalOpenRef.current();
			const pathname = window.location.pathname;

			const primaryRoute =
				primaryRoutes[appName] &&
				Object.values(primaryRoutes[appName]).includes(pathname);

			const { onBackButtonClick: configOnBackButtonClick } =
				navigationConfigRef.current;

			if (isModalOpen) {
				closeModalRef.current();
				return;
			}

			// Match long-standing app behavior: root routes exit before WebView history pops.
			if (pathname === '/login' || pathname === '/') {
				void App.exitApp();
				return;
			}
			if (primaryRoute) {
				navigateRef.current('/');
				return;
			}
			if (configOnBackButtonClick) {
				configOnBackButtonClick();
				return;
			}
			if (webViewCanGoBack) {
				navigateRef.current(-1);
				return;
			}
			void App.exitApp();
		}

		const listenerPromise = App.addListener(
			'backButton',
			handleCapacitorHardwareBack,
		);

		return () => {
			void listenerPromise.then(handle => void handle.remove());
		};
	}, []);

	/* Mitigate rare blank WebView on resume (opacity nudge when app becomes active). */

	useEffect(() => {
		//app-state-change
		if (!isNativeAppShell()) return;

		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		function onAppStateChange(params) {
			if (params.state === 'active') {
				document.body.style.opacity = '0.99';
				timeoutId = setTimeout(() => {
					document.body.style.opacity = '1';
				}, 1000);
			}
		}

		bridge.addEventListener('app-state-change', onAppStateChange);

		return () => {
			timeoutId !== null && clearTimeout(timeoutId);
			bridge.removeEventListener('app-state-change', onAppStateChange);
		};
	}, [bridge]);

	useEffect(() => {
		if (!isNativeAppShell()) return;

		function onDeeplinkReceived(params: Record<string, any>) {
			// Handle the deeplink here
			// You can navigate to the deeplink path or perform any other action

			if (params.deeplink) {
				navigate(params.deeplink);
			}
		}

		bridge.addEventListener('DeeplinkReceived', onDeeplinkReceived);

		return () => {
			bridge.removeEventListener('DeeplinkReceived', onDeeplinkReceived);
		};
	}, [navigate, bridge]);

	return (
		<PlatformContext.Provider value={platformData}>
			{children}
		</PlatformContext.Provider>
	);
}
